// @flow
import { captureException } from '@sentry/react'
import api from 'api'
import Loading from 'components/Loading'
import ModalFormUnavailable from 'components/Modal/ModalFormUnavailable'
import Noindex from 'components/Noindex'
import { useFilters } from 'hooks/useQuery'
import * as React from 'react'
import { Helmet } from 'react-helmet'
import { toast } from 'react-toastify'
import Page from 'ui/organisms/Page'
import { hasText, momentLocal, removeNoindexMeta } from 'utils/helpers'
import {
  dateOfBirthRequiredSchema,
  emailSchema,
  firstNameSchema,
  lastNameSchema,
  schemasValidate,
} from 'utils/schemas'
import { ZentakeContext } from 'utils/ZentakeContext'

import PatientFormContent from './PatientFormContent'

type Props = {
  title: string,
  onSubmit: (customer?: Customer) => Promise<void>,
  unavailable?: boolean,
  resourceType: 'form' | 'packet' | 'document',
  resourceId: string,
  messageType?: 'public_response' | 'kiosk_response',
}

const initialFormError = {
  firstName: false,
  lastName: false,
  dateOfBirth: false,
}

const initialFormErrorLabel = {
  firstName: undefined,
  lastName: undefined,
  dateOfBirth: undefined,
}

type PatientFormParams = {
  firstName?: string,
  lastName?: string,
  email?: string,
  birthDate?: string,
  auto?: boolean,
}

function PatientForm({
  title,
  onSubmit,
  unavailable = false,
  resourceType,
  resourceId,
  messageType,
}: Props): React.Node {
  const { teamConfig } = React.useContext(ZentakeContext)
  const { filters }: { filters: PatientFormParams } = useFilters()

  const clientLabel = teamConfig?.client_label

  const maxValidDate = React.useRef(momentLocal().format('MM-DD-YYYY')).current
  const [firstName, setFirstName] = React.useState(filters.firstName || '')
  const [email, setEmail] = React.useState(filters.email || '')
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [lastName, setLastName] = React.useState(filters.lastName || '')
  const [dateOfBirth, setDateOfBirth] = React.useState(
    filters.birthDate || undefined
  )
  const [formError, setFormError] = React.useState(initialFormError)
  const [formErrorLabel, setFormErrorLabel] = React.useState(
    initialFormErrorLabel
  )

  const validateFirstName = React.useCallback(async () => {
    schemasValidate(
      [['firstName', firstName, firstNameSchema]],
      setFormError,
      setFormErrorLabel
    )
  }, [firstName])

  const validateLastName = React.useCallback(async () => {
    schemasValidate(
      [['lastName', lastName, lastNameSchema]],
      setFormError,
      setFormErrorLabel
    )
  }, [lastName])

  const validateEmail = React.useCallback(async () => {
    schemasValidate(
      [['email', email, emailSchema]],
      setFormError,
      setFormErrorLabel
    )
  }, [email])

  const onUpdateDateOfBirth = React.useCallback(
    (input) => {
      if (!hasText(input)) return setDateOfBirth(undefined)

      const dateInput = new Date(input)
      const isValidDate = dateInput instanceof Date && !isNaN(dateInput)
      setDateOfBirth(isValidDate ? input : undefined)

      schemasValidate(
        [['dateOfBirth', dateInput, dateOfBirthRequiredSchema]],
        setFormError,
        setFormErrorLabel
      )
    },
    [setFormError, setFormErrorLabel]
  )

  const validateDateOfBirth = React.useCallback(async () => {
    if (!dateOfBirth) return

    schemasValidate(
      [['dateOfBirth', dateOfBirth, dateOfBirthRequiredSchema]],
      setFormError,
      setFormErrorLabel
    )
  }, [dateOfBirth])

  const validateForm = React.useCallback(async () => {
    const schemas = [
      ['firstName', firstName, firstNameSchema],
      ['lastName', lastName, lastNameSchema],
    ]
    if (teamConfig?.ask_dob) {
      schemas.push(['dateOfBirth', dateOfBirth, dateOfBirthRequiredSchema])
    }

    if (teamConfig?.ask_email) {
      schemas.push(['email', email, emailSchema])
    }

    return schemasValidate(schemas, setFormError, setFormErrorLabel)
  }, [
    dateOfBirth,
    email,
    firstName,
    lastName,
    teamConfig?.ask_dob,
    teamConfig?.ask_email,
  ])

  React.useEffect(() => removeNoindexMeta, [])

  const getOrCreateCustomer = React.useCallback(async () => {
    try {
      return await api.getOrCreateCustomer({
        firstName,
        lastName,
        dateOfBirth,
        email,
        resourceType,
        resourceId,
        messageType,
      })
    } catch (error) {
      captureException(error)
      toast.error('Something went wrong. Please try again later.')
      return null
    }
  }, [
    firstName,
    lastName,
    dateOfBirth,
    email,
    resourceType,
    resourceId,
    messageType,
  ])

  const handleSubmit = React.useCallback(async () => {
    setIsSubmitting(true)
    const isFormValid = await validateForm()
    if (isFormValid) {
      const customer = await getOrCreateCustomer()
      if (customer) {
        await onSubmit(customer)
      } else {
        setIsSubmitting(false)
      }
    }
    setIsSubmitting(false)
  }, [getOrCreateCustomer, onSubmit, validateForm])

  React.useEffect(() => {
    if (filters.auto === true) {
      handleSubmit()
    }
  }, [filters.auto, handleSubmit])

  if (unavailable) {
    return (
      <ModalFormUnavailable
        hideButton
        isVisible
        description="Sorry, this form is not available anymore"
        title="Form not available"
      />
    )
  }

  if (!title) return <Loading />

  return (
    <Page
      centerTitle
      container
      marginTop
      subtitle="Please fill out your info below to get started"
      title={title}
      translate="no"
    >
      <Noindex />
      <Helmet>
        <title>{`${title}/Zentake`}</title>
        <meta content="notranslate" name="google" />
        <meta content="en" http-equiv="Content-Language" />
      </Helmet>
      <PatientFormContent
        clientLabel={clientLabel}
        dateOfBirth={dateOfBirth}
        email={email}
        firstName={firstName}
        formError={formError}
        formErrorLabel={formErrorLabel}
        handleSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        lastName={lastName}
        maxValidDate={maxValidDate}
        onUpdateDateOfBirth={onUpdateDateOfBirth}
        setEmail={setEmail}
        setFirstName={setFirstName}
        setLastName={setLastName}
        teamConfig={teamConfig}
        validateDateOfBirth={validateDateOfBirth}
        validateEmail={validateEmail}
        validateFirstName={validateFirstName}
        validateLastName={validateLastName}
      />
    </Page>
  )
}

export default PatientForm
